<template>
  <div>
    <div class="form-group w-100 mb-5">
      <label class="d-block">{{ $t('product.products') }}:</label>
      <v-select
        :options="availableProducts"
        v-model="selected_product"
        label="name"
        :reduce="product => ({ id: product.id, name: product.name, net_content: product.net_content, net_content_unit_code: product.net_content_unit_code, price_purchase: product.price_purchase, estimated_price: product.estimated_price, equivalent_products_count: product.equivalent_products_count || 0, packaging: product.packaging })"
        :filterable="false"
        :clear-search-on-select="false"
        :close-on-select="true"
        @search="onProductSearch"
        @input="selectingProduct"
        :placeholder="$t('product.search_ingredient_product')"
      >
        <template #spinner="{ loading }">
          <div v-if="loading || searchingProducts" class="vs__spinner"></div>
        </template>
        <template v-slot:option="product">
          <span>{{ product.name }}</span>
          <template v-if="product.packaging">
            <CBadge color="warning" class="ml-1">
              <CIcon name="cil-layers" size="sm" /> {{ getPackagingTypeLabel(product.packaging.packaging_type_code) }} x{{ product.packaging.packaged_product_quantity }}
            </CBadge>
            <small v-if="product.net_content" class="d-block text-muted">
              {{ product.net_content }} {{ getUnitCodeLabel(product.net_content_unit_code) }} / {{ $t('global.piece') }}
            </small>
          </template>
          <small v-else-if="product.net_content" class="text-muted ml-1">
            ({{ product.net_content }} {{ getUnitCodeLabel(product.net_content_unit_code) }})
          </small>
        </template>
        <span slot="no-options">{{ noProductOptionsText }}</span>
      </v-select>
    </div>

    <div class="table-ingredients table-responsive-md">
      <table class="table">
        <colgroup>
          <col>
          <col>
          <col>
          <col>
          <col>
          <col>
          <col style="background-color: #C4EBD1aa;">
        </colgroup>
        <thead>
          <tr>
            <th scope="col" style="min-width: 36px;width: 36px;text-align: center"></th>
            <th scope="col" style="min-width: 200px;">{{ $t('product.product_name') }}</th>
            <th scope="col" style="min-width: 120px;width: 120px; text-align: center">{{ $t('product.ingredients.can_be_excluded') }}</th>
            <th scope="col" style="min-width: 120px;width: 120px; text-align: center">{{ $t('product.cook_station') }}</th>
            <th v-if="isRestaurant" style="min-width: 180px">{{ $t('station.assignment') }}</th>

            <th scope="col" style="min-width: 140px;width: 160px; text-align: center">{{ $t('product.ingredients.estimated_price') }}</th>
            <th scope="col" style="min-width: 140px;width: 160px; text-align: center">{{ $t('product.ingredients.usage_rate') }}</th>

            <th scope="col" style="min-width: 260px;width: 260px;">{{ $t('product.net_content') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in productIngredients" :key="item.id">
            <!-- Actions -->
            <td class="py-1 px-0 text-center">
              <CButton
                variant="ghost"
                shape="pill"
                size="sm"
                color="danger"
                @click="deleteItem(index)"
              >
                <CIcon name="cil-trash"/>
              </CButton>
            </td>

            <td>
              <em>
                <ProductNameWithActions
                  :id="item.id"
                  :name="item.name"
                  :name_short="item.name_short"
                  :is_deleted="item.is_deleted"
                  :equivalent-products-count="item.equivalent_products_count"
                  @open="openProduct"
                />
              </em>
              <button
                v-if="item.equivalent_products_count"
                type="button"
                class="ingredient-equivalents-chip ml-2"
                :title="$t('product.equivalent_products')"
                @click="openIngredientEquivalents(item)"
              >
                <CIcon name="cil-link" />
                <span>{{ $tc('product.equivalent_products_available', item.equivalent_products_count) }}</span>
              </button>

              <template v-if="item.packaging">
                <CBadge color="warning" class="ml-1">
                  <CIcon name="cil-layers" size="sm" /> {{ getPackagingTypeLabel(item.packaging.packaging_type_code) }} x{{ item.packaging.packaged_product_quantity }}
                </CBadge>
                <small v-if="item.net_content" class="d-block text-muted mt-1">
                  {{ item.net_content }} {{ getUnitCodeLabel(item.net_content_unit_code) }} / {{ $t('global.piece') }}
                </small>
              </template>
              <small v-else-if="item.net_content" class="d-block text-muted">
                {{ item.net_content }} {{ getUnitCodeLabel(item.net_content_unit_code) }}
              </small>
            </td>

            <!-- Can Be Excluded -->
            <td class="text-center">
              <CSwitch
                :checked.sync="item.pivot.can_be_excluded"
                :labelOn="$t('global.switch_yes')"
                :labelOff="$t('global.switch_no')"
                color="success"
                shape="pill"
                :lazy="false"
              />
            </td>

            <!-- Cook Station -->
            <td class="text-center">
              <CSwitch
                :checked.sync="item.cook_station"
                :labelOn="$t('global.switch_yes')"
                :labelOff="$t('global.switch_no')"
                color="success"
                shape="pill"
                :lazy="false"
              />
            </td>

            <td v-if="isRestaurant" class="station-assignment-cell">
              <RestaurantStationSelect v-model="item.station_id" :firm-id="selectedFirm.id" :cook-station="item.cook_station"
                :conflict="item.station_conflict" :product-name="item.name || ''" compact />
            </td>
            <td class="text-center">
              <span v-if="computedEstimatedPrice(item) !== null">{{ computedEstimatedPrice(item) | toCurrency(currency) }}</span>
            </td>
            <td class="text-center">
              <span v-if="computedUsageRate(item) !== null">{{ Number(computedUsageRate(item)).toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 2 }) }}</span>
            </td>

            <!-- Net Content -->
            <td>
              <div class="d-flex flex-row">
                <CInput
                  style="width: 100px;"
                  class="mb-0 mr-1"
                  :lazy="false"
                  :value.sync="item.pivot.net_content"
                  placeholder="0.00"
                  type="number"
                  step="0.01"
                  pattern="^\d+(?:\.\d{1,2})?$"
                  onblur="(function(el){el.value=parseFloat(el.value).toFixed(2);})(this)"
                />

                <CSelect
                  style="width: 150px;"
                  class="mb-0"
                  :placeholder="$t('global.please_select')"
                  :lazy="false"
                  :value.sync="item.pivot.net_content_unit_code"
                  :options="getCompatibleUnits(item)"
                />
              </div>
            </td>
          </tr>

          <tr v-if="productIngredients.length === 0">
            <td colspan="7">
              <div class="text-center my-5">
                <h2> {{ $t('global.no_items_found') }}
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" role="img" class="text-danger mb-2 c-icon c-icon-custom-size" width="30"><path fill="var(--ci-primary-color, currentColor)" d="M425.706,86.294A240,240,0,0,0,86.294,425.705,240,240,0,0,0,425.706,86.294ZM256,48A207.1,207.1,0,0,1,391.528,98.345L98.345,391.528A207.1,207.1,0,0,1,48,256C48,141.309,141.309,48,256,48Zm0,416a207.084,207.084,0,0,1-134.986-49.887l293.1-293.1A207.084,207.084,0,0,1,464,256C464,370.691,370.691,464,256,464Z" class="ci-primary"></path></svg>
                </h2>
              </div>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <th colspan="4"></th>
            <td class="text-center pt-3">
              <h5>{{ totalEstimatedPrice | toCurrency(currency) }}</h5>
            </td>
            <th colspan="2"></th>
          </tr>
        </tfoot>
      </table>
    </div>

  </div>
</template>

<script>
import RestaurantStationSelect from '@/components/RestaurantStationSelect.vue';

import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import ProductNameWithActions from "./ProductNameWithActions.vue";
import {
  getUnitCodeLabel as _getUnitCodeLabel,
  getCompatibleUnits as _getCompatibleUnits,
  computeUsageRate,
  computeEstimatedPrice,
} from "../helper/unitConversions";

export default {
  name: "product-ingredients",
  components: {
    RestaurantStationSelect,
    vSelect,
    ProductNameWithActions,
  },

  props: {
    productId: { default: null, type: Number },
    ingredients: { type: Array, default() { return []; } },
    measure_codes: { type: Array, default() { return []; } },
    currency: { type: String, default: 'EUR' },
    parentNetContent: { default: null },
    parentNetContentUnitCode: { default: null },
  },

  data() {
    return {
      selected_product: null,
      allFirmProducts: [],
      productSearch: "",
      searchProductsTimer: null,
      searchingProducts: false,
    };
  },

  computed: {
    productIngredients: {
      get() {
        return this.ingredients;
      },
      set(newValue) {
        this.$emit("update:ingredients", newValue);
      }
    },
    availableProducts() {
      const selectedIds = new Set(this.productIngredients.map((item) => Number(item.id)));
      return this.allFirmProducts.filter((product) => !selectedIds.has(Number(product.id)));
    },
    totalEstimatedPrice() {
      return this.productIngredients.reduce((sum, item) => {
        const price = this.computedEstimatedPrice(item);
        return sum + (Number(price) || 0);
      }, 0);
    },
    noProductOptionsText() {
      if (!this.productSearch || this.productSearch.length < 2) {
        return this.$t('product.search_ingredient_product_min_chars');
      }

      if (this.searchingProducts) {
        return this.$t('global.please_wait');
      }

      return this.$t('global.no_items_found');
    },
  },

  mounted() {
    // Global product update event'ini dinle
    this.$root.$on('product-updated', this.handleProductUpdated);
  },

  beforeDestroy() {
    if (this.searchProductsTimer) {
      clearTimeout(this.searchProductsTimer);
    }

    // Event listener'ı temizle
    this.$root.$off('product-updated', this.handleProductUpdated);
  },

  methods: {
    deleteItem(index) {
      this.productIngredients.splice(index, 1);

      // this.productIngredients = this.removeIngredients(this.productIngredients, item.id);
      // this.productIngredients = this.productIngredients.filter(function (item) {
      //   return item.ingredient_id != ingredient_id;
      // });
    },

    selectingProduct() {
      if (this.selected_product) {
        this.addProductIngredients(this.selected_product)
        this.selected_product = null;
      }
    },

    addProductIngredients(ingredient) {
      const unitCode = ingredient.net_content_unit_code || 'H87';
      this.productIngredients.push({
        id: ingredient.id,
        name: ingredient.name,
        net_content: ingredient.net_content,
        net_content_unit_code: ingredient.net_content_unit_code,
        price_purchase: ingredient.price_purchase,
        estimated_price: ingredient.estimated_price || null,
        equivalent_products_count: ingredient.equivalent_products_count || 0,
        packaging: ingredient.packaging || null,
        cook_station: ingredient.cook_station,
        station_id: ingredient.station_id ?? null,
        station_conflict: ingredient.station_conflict ?? null,
        pivot: {
          net_content: null,
          net_content_unit_code: unitCode,
          can_be_excluded: false,
          ingredient_id: ingredient.id,
        }
      });
    },

    async searchProducts(search, orderBy = 'name', direction = 'asc') {
      if (!search || search.length < 2) {
        this.allFirmProducts = [];
        return;
      }

      const params = {
        type: 'supplied-products,own-production',
        orderBy: orderBy,
        direction: direction,
        fields: 'net_content,net_content_unit_code,price_purchase,cook_station,station_id,station_conflict',
        with: 'packaging',
        search,
        limit: 30,
      };

      // Exclude this product
      if (this.productId) {
        params.excluded_products = JSON.stringify([this.productId]);
      }

      this.searchingProducts = true;

      try {
        const response = await this.$axios.get(
          this.$backend
            .get("FIRM", "PRODUCT_LIST", this.selectedFirm.id)
            .replace("/{related_firm_id}", ""),
          { params }
        );

        this.allFirmProducts = response.data || [];
      } finally {
        this.searchingProducts = false;
      }
    },

    onProductSearch(search) {
      this.productSearch = search || "";

      if (this.searchProductsTimer) {
        clearTimeout(this.searchProductsTimer);
      }

      this.searchProductsTimer = setTimeout(() => {
        this.searchProducts(this.productSearch);
      }, 300);
    },

    handleProductUpdated(product) {
      const index = this.productIngredients.findIndex(p => p.id === product.id);

      if (index > -1) {
        const currentItem = this.productIngredients[index];
        const incomingPivot = Object.prototype.hasOwnProperty.call(product, 'pivot') ? product.pivot : undefined;
        const mergedPivot = currentItem.pivot
          ? { ...currentItem.pivot, ...(incomingPivot || {}) }
          : (incomingPivot || null);

        this.$set(this.productIngredients, index, {
          ...currentItem,
          ...product,
          pivot: mergedPivot
        });
      }
    },

    openProduct(productId) {
      this.$emit("open-product", productId);
    },

    openIngredientEquivalents(item) {
      this.$store.commit('set', ['asideProductQuickEditTab', 'equivalents']);
      this.$store.commit('set', ['asideProductQuickEditId', item.id]);
    },

    getUnitCodeLabel(code) {
      return _getUnitCodeLabel(this.measure_codes, code);
    },

    getCompatibleUnits(item) {
      return _getCompatibleUnits(this.measure_codes, item.net_content_unit_code);
    },

    computedUsageRate(item) {
      return computeUsageRate(
        item.pivot?.net_content, item.pivot?.net_content_unit_code,
        item.net_content, item.net_content_unit_code,
        this.measure_codes, item.usage_rate || null
      );
    },

    computedEstimatedPrice(item) {
      const usageRate = this.computedUsageRate(item);
      let pricePurchase = parseFloat(item.price_purchase) || 0;
      // For packaged products, use unit price instead of package price
      if (pricePurchase && item.packaging && item.packaging.packaged_product_quantity > 0) {
        pricePurchase = pricePurchase / item.packaging.packaged_product_quantity;
      }
      // For own-production products without purchase price, use estimated_price as effective price
      if (!pricePurchase && item.estimated_price) {
        pricePurchase = parseFloat(item.estimated_price);
      }
      return computeEstimatedPrice(pricePurchase, usageRate, item.estimated_price || null);
    },

    getPackagingTypeLabel(code) {
      if (!code) return '';
      return this.$t('packaging.type_codes.' + code);
    },
  },
};
</script>

<style scoped>
.station-assignment-cell {
  vertical-align: middle;
}
.station-assignment-cell .restaurant-station-select {
  min-width: 0;
}
</style>

<style>
.table-ingredients th {
  border-top: none;
}
.table-ingredients th, .table-ingredients td {
  padding: 0 10px;
  vertical-align: middle !important;
}
.table-ingredients thead tr {
  height: 32px;
}
.ingredient-equivalents-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  max-width: 120px;
  padding: 2px 6px;
  border: 1px solid #b8d6ff;
  border-radius: 14px;
  background: #f2f7ff;
  color: #1f64c8;
  font-size: 0.72rem;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}
.ingredient-equivalents-chip span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
